import { useState, useCallback } from 'preact/hooks';
import type { I18nStrings, LeaveMessageRequest } from '@bitio-builder/shared';

interface LeaveMessageFormProps {
  onSubmit: (data: LeaveMessageRequest) => Promise<boolean>;
  strings: I18nStrings;
  /** Shows "outside office hours" messaging instead of default */
  isOutsideHours?: boolean;
  /** Compact mode for inline display */
  compact?: boolean;
  /** Callback to go back to mode selector */
  onBack?: () => void;
}

const containerStyle: Record<string, string | number> = {
  padding: '24px',
  background: 'white',
  flex: 1,
  overflowY: 'auto',
};

const compactContainerStyle: Record<string, string | number> = {
  padding: '12px',
  background: 'var(--bb-muted-bg, #F9FAFB)',
  borderRadius: '12px',
  margin: '0 12px 12px',
  maxHeight: '300px',
  overflowY: 'auto',
  flexShrink: 0,
};

const headerStyle: Record<string, string | number> = {
  marginBottom: '20px',
};

const titleStyle: Record<string, string | number> = {
  fontSize: '16px',
  fontWeight: 600,
  color: 'var(--bb-foreground, #1A1C17)',
  marginBottom: '4px',
};

const compactTitleStyle: Record<string, string | number> = {
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--bb-foreground, #1A1C17)',
  marginBottom: '4px',
};

const subtitleStyle: Record<string, string | number> = {
  fontSize: '12px',
  color: 'var(--bb-muted, #6B7280)',
};

const formStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const compactFormStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const compactHeaderStyle: Record<string, string | number> = {
  marginBottom: '10px',
};

const compactInputStyle: Record<string, string | number> = {
  padding: '8px 12px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
};

const compactTextareaStyle: Record<string, string | number> = {
  ...compactInputStyle,
  minHeight: '60px',
  resize: 'none',
};

const compactButtonStyle: Record<string, string | number> = {
  width: '100%',
  padding: '10px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '4px',
};

const fieldStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const labelStyle: Record<string, string | number> = {
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--bb-foreground, #1A1C17)',
};

const inputStyle: Record<string, string | number> = {
  padding: '10px 14px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
};

const textareaStyle: Record<string, string | number> = {
  ...inputStyle,
  minHeight: '80px',
  resize: 'vertical',
};

const inputErrorStyle: Record<string, string | number> = {
  ...inputStyle,
  borderColor: 'var(--bb-error, #DC2626)',
};

const errorMessageStyle: Record<string, string | number> = {
  fontSize: '11px',
  color: 'var(--bb-error, #DC2626)',
};

const submitButtonStyle: Record<string, string | number> = {
  width: '100%',
  padding: '14px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '4px',
};

const successStyle: Record<string, string | number> = {
  textAlign: 'center',
  padding: '24px',
};

const successIconStyle: Record<string, string | number> = {
  width: '48px',
  height: '48px',
  background: 'var(--bb-success, #059669)',
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
};

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const backButtonStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  marginBottom: '16px',
  background: 'transparent',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  color: 'var(--bb-muted, #6B7280)',
  fontFamily: 'inherit',
};

export function LeaveMessageForm({
  onSubmit,
  strings,
  isOutsideHours = false,
  compact = false,
  onBack,
}: LeaveMessageFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const s = strings.leaveMessage;

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    const v = s.validation;

    if (!formData.name.trim()) {
      newErrors.name = v.nameRequired;
    }

    if (!formData.email.trim()) {
      newErrors.email = v.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = v.emailInvalid;
    }

    if (!formData.message.trim()) {
      newErrors.message = v.messageRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, s]);

  const handleSubmit = useCallback(
    async (e: Event) => {
      e.preventDefault();

      if (!validate()) return;

      setIsSubmitting(true);

      try {
        const success = await onSubmit({
          name: formData.name.trim(),
          email: formData.email.trim(),
          company: formData.company.trim() || undefined,
          message: formData.message.trim(),
          metadata: {
            referrer: window.location.href,
            userAgent: navigator.userAgent,
          },
        });

        if (success) {
          setIsSuccess(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validate, onSubmit]
  );

  const handleChange = useCallback((field: keyof FormData) => {
    return (e: Event) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      setFormData((prev) => ({ ...prev, [field]: target.value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }, []);

  if (isSuccess) {
    return (
      <div style={compact ? compactContainerStyle : containerStyle}>
        <div style={successStyle}>
          <div style={successIconStyle}>
            <CheckIcon />
          </div>
          <p style={{ color: 'var(--bb-foreground)', fontWeight: 500 }}>{s.success}</p>
        </div>
      </div>
    );
  }

  const title = isOutsideHours ? s.outsideHoursTitle : s.title;
  const subtitle = isOutsideHours ? s.outsideHoursSubtitle : s.subtitle;

  // Choose styles based on compact mode
  const currentInputStyle = compact ? compactInputStyle : inputStyle;
  const currentInputErrorStyle = compact
    ? { ...compactInputStyle, borderColor: 'var(--bb-error, #DC2626)' }
    : inputErrorStyle;
  const currentTextareaStyle = compact ? compactTextareaStyle : textareaStyle;
  const currentButtonStyle = compact ? compactButtonStyle : submitButtonStyle;

  return (
    <div style={compact ? compactContainerStyle : containerStyle}>
      {!compact && onBack && (
        <button type="button" style={backButtonStyle} onClick={onBack}>
          <BackIcon />
          {s.back}
        </button>
      )}

      <div style={compact ? compactHeaderStyle : headerStyle}>
        <h3 style={compact ? compactTitleStyle : titleStyle}>{title}</h3>
        {!compact && <p style={subtitleStyle}>{subtitle}</p>}
      </div>

      <form style={compact ? compactFormStyle : formStyle} onSubmit={handleSubmit}>
        <div style={fieldStyle}>
          <label style={labelStyle}>{s.name} *</label>
          <input
            type="text"
            value={formData.name}
            onInput={handleChange('name')}
            style={errors.name ? currentInputErrorStyle : currentInputStyle}
            disabled={isSubmitting}
          />
          {errors.name && <span style={errorMessageStyle}>{errors.name}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{s.email} *</label>
          <input
            type="email"
            value={formData.email}
            onInput={handleChange('email')}
            style={errors.email ? currentInputErrorStyle : currentInputStyle}
            disabled={isSubmitting}
          />
          {errors.email && <span style={errorMessageStyle}>{errors.email}</span>}
        </div>

        {!compact && (
          <div style={fieldStyle}>
            <label style={labelStyle}>{s.company}</label>
            <input
              type="text"
              value={formData.company}
              onInput={handleChange('company')}
              style={currentInputStyle}
              disabled={isSubmitting}
            />
          </div>
        )}

        <div style={fieldStyle}>
          <label style={labelStyle}>{s.message} *</label>
          <textarea
            value={formData.message}
            onInput={handleChange('message')}
            style={errors.message ? { ...currentTextareaStyle, borderColor: 'var(--bb-error, #DC2626)' } : currentTextareaStyle}
            placeholder={s.messagePlaceholder}
            disabled={isSubmitting}
          />
          {errors.message && <span style={errorMessageStyle}>{errors.message}</span>}
        </div>

        <button
          type="submit"
          style={{ ...currentButtonStyle, opacity: isSubmitting ? 0.7 : 1 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? s.submitting : s.submit}
        </button>
      </form>
    </div>
  );
}
